import type { ClaimWithRelations } from "../../api/use-claim";
import { isLeisureOffice } from "../../lib/leisure-office";

/** Port of the `ClaimOverview` interface of pages/plugin/AgentFeedback.vue. */
export interface ClaimOverview {
  claimId: string;
  feedbackId?: string;
  pnrId: string | null;
  flightIdentifier: string;
  ec261CompensationAmount: number;
  travelAgencyCompany:
    | ClaimWithRelations["legalSpecification"]["impactedSegment"]["reservation"]["travelAgencyCompany"]
    | null;
  claimType: string;
  travelAgencyCompanyName?: string | null;
  stateUpdatedAt?: Date;
  isOfficeB2b: boolean;
  /** Office ID explicitement loisir : ni mention ni choix d'entreprise. */
  isLeisureOffice: boolean;
  form: ClaimWithRelations["agentFeedback"] | null;
  passengers: {
    id: string;
    /** Nullable in database — a passenger can be known by its last name only, or neither. */
    firstName: string | null;
    lastName: string | null;
  }[];
}

/** Port of getClaimOverview (AgentFeedback.vue). */
export function getClaimOverview(claim: ClaimWithRelations): ClaimOverview {
  const reservation = claim.legalSpecification.impactedSegment.reservation;
  return {
    claimId: claim.id,
    feedbackId: claim.agentFeedback?.id,
    pnrId: reservation.gdsPnrId,
    flightIdentifier: claim.legalSpecification.effectiveFlight.flightIdentifier,
    ec261CompensationAmount: claim.legalSpecification.ec261CompensationAmount,
    travelAgencyCompany: reservation.travelAgencyCompany,
    claimType: claim.claimType.name,
    travelAgencyCompanyName: reservation.travelAgencyCompany?.displayName,
    stateUpdatedAt: claim.stateUpdatedAt ? new Date(claim.stateUpdatedAt) : undefined,
    isOfficeB2b: Boolean(reservation.travelAgencyOffice.isBusinessTravel),
    isLeisureOffice: isLeisureOffice(reservation.travelAgencyOffice),
    passengers: claim.usersClaims.map(({ user }) => ({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
    })),
    form: claim.agentFeedback,
  };
}

export interface AgentFeedbackStep {
  stepNumber: number;
  name: string;
  description: string;
}

/** Steps of the manual agent-feedback flow (AgentFeedback.vue). */
export const AGENT_FEEDBACK_STEPS: AgentFeedbackStep[] = [
  { stepNumber: 0, name: "Confirmation", description: "Valider le dossier" },
  { stepNumber: 1, name: "Préjudices", description: "Établir les demandes supplémentaires" },
  { stepNumber: 2, name: "Signatures", description: "Attente signature cessions de créances" },
];

/** Claim states meaning the flow reached the signature step. */
export const SIGNATURE_STATES = [
  "CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_CONTRACT_SIGNATURES",
  "CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_CONTRACT_SIGNATURES_B2C_AND_B2B",
  "CLAIM_VALIDATION.WAIT_FOR_ENTREPRISE_SIGNATURE",
];

interface StepDerivationInput {
  agentFeedback?: {
    additionalLoss?: string | null;
    validationOptionId?: number | null;
  } | null;
  claimStateType: string;
}

/** Port of getStep (AgentFeedback.vue) — derives the initial step from the claim. */
export function getInitialStep({ agentFeedback, claimStateType }: StepDerivationInput): number {
  if (agentFeedback?.additionalLoss || SIGNATURE_STATES.includes(claimStateType)) {
    return 2; // Download / Generate receivable document
  }
  if (agentFeedback?.validationOptionId) {
    return 1;
  }
  if (claimStateType === "CLAIM_VALIDATION.WAIT_FOR_AGENT_FEEDBACK") {
    return 0; // Agent confirmation
  }
  return 1; // AdditionalLoss form
}
